import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import { Factory } from "vexflow";
import Markdown from 'react-markdown';
import file from './README'

const letters = ["C", "D", "E", "F", "G", "A", "B"];

const diatonic = [], sharps = [], flats = [];
for (let i = 0; i <= 9 ; i++) {
  diatonic.push(...letters.map( a => a + i ));
  sharps.push(...letters.map( a => a + '#' + i ));
  flats.push(...letters.map( a => a + 'b' + i ));
}
const downcaseFlats = flats.map(x => x.toLowerCase());
const matrix = [flats, diatonic, sharps];
const topIndex = diatonic.length - 1;

// semitones above C0; A4 = 57
const offsets = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
const semi = (n) =>
  offsets[n[0].toUpperCase()] +
  (n.match('#') ? 1 : n.match(/b/) ? -1 : 0) +
  12 * +n[n.length - 1];

// playable ranges, low to high
const ranges = {
  piano: ['A0', 'C8'],
  guitar: ['E2', 'E6'],
  cello: ['C2', 'C6'],
  viola: ['C3', 'E6'],
  violin: ['G3', 'A7'],
  flute: ['C4', 'D7'],
};

function App() {
  const [note, setNote] = useState('C4');
  const [inputNote, setInputNote] = useState('');
  const [displayNote, setDisplayNote] = useState('C4');
  const [index, setIndex] = useState(28);
  const [accidental, setAccidental] = useState(0);
  const [instrument, setInstrument] = useState('');
  const [pitchStandard, setPitchStandard] = useState(440);

  const updateSvg = (n) => {
    if (!!n.match(/^[A-Ga-g]{1}[b#]{0,1}\d{1}$/)) {
      setNote(n);
      if (!!n.match('#')) {
        setIndex(sharps.indexOf(n));
        setAccidental(1);
      } else
      if (!!n.match('b')) {
        setIndex(downcaseFlats.indexOf(n.toLowerCase()));
        setAccidental(-1);
      } else {
        setIndex(diatonic.indexOf(n));
        setAccidental(0);
      }
      setDisplayNote((n.replace(/(?<=[a-gA-G])b(?=\d{1})/, '♭').replace('#', '♯')));
    }
  }

  // draw into #output, caching each note’s svg so revisits paste instead of redraw
  const svgCache = useRef({});
  useEffect(() => {
    const output = document.querySelector('#output');
    if (svgCache.current[note]) {
      output.innerHTML = svgCache.current[note];
      return;
    }
    output.innerText = '';
    const factory = new Factory({
      renderer: { elementId: "output", width: 175, height: 375 },
    });
    const score = factory.EasyScore();
    factory
        .System()
        .addStave({
            voices: [
              score.voice(score.notes(`C${accidental === 0 ? '#' : ''}5/q, ${note}/q, C#5/h`)),
            ],
        })
        .addClef("treble")
    factory.draw();
    output.querySelector('svg').setAttribute("viewBox", "0 -120 175 375");
    svgCache.current[note] = output.innerHTML;
  }, [note, accidental]);

  const inputRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      const { key } = e;
      if (key === 'Escape' ) {
        inputRef.current.focus();
        setInputNote('');
      }
      if (key === 'r' ) {
        setInputNote(randomNote());
      }
      if (!!key.match(/^Arrow/)) { e.preventDefault(); }
      const staff = matrix[accidental + 1];
      if (key === 'ArrowUp' && index < topIndex) { setInputNote(staff[index + 1]); }
      if (key === 'ArrowDown' && index > 0) { setInputNote(staff[index - 1]); }
      if (key === 'ArrowRight' && index + 7 <= topIndex) { setInputNote(staff[index + 7]); }
      if (key === 'ArrowLeft' && index - 7 >= 0) { setInputNote(staff[index - 7]); }
    }
    window.addEventListener('keydown', handler);
    return () => {
      // This is the cleanup function
      window.removeEventListener('keydown', handler);
    };
  }, [index, accidental]);

  useEffect(() => {
    updateSvg(inputNote);
  }, [inputNote]);

  const randomNote = () => matrix[Math.floor(3*Math.random())][Math.floor((topIndex + 1)*Math.random())];

  const insertFlat = () => {
    if (diatonic.indexOf(inputNote.toUpperCase()) > -1) { setInputNote(flats[diatonic.indexOf(inputNote.toUpperCase())])} else
    if (!!inputNote.match('#')) { setInputNote(inputNote.replace('#', 'b'))} else
    if (inputNote.match(/^[a-gA-G]$/)) { setInputNote(inputNote.toUpperCase() + 'b')};
  }

  const insertSharp = () => {
    if (inputNote.match(/^[a-gA-G]$/)) { setInputNote(inputNote.toUpperCase() + '#')} else
    if (diatonic.indexOf(inputNote.toUpperCase()) > -1) { setInputNote(sharps[diatonic.indexOf(inputNote.toUpperCase())])} else
    if (!!inputNote.match(/^[A-Ga-g]{1}[b]{0,1}\d{0,1}$/)) { setInputNote(inputNote.replace(/(?<=[a-gA-G])b(?=\d{0,1})/, '#'))};
  }

  const naturalize = () => {
    if (!!inputNote.match('#')) { setInputNote(inputNote.replace('#', ''))} else
    if (!!inputNote.match(/^[a-gA-G]b$/)) { setInputNote(inputNote[0]) } else
    {setInputNote(inputNote.replace(/(?<=[a-gA-G])b(?=\d{1})/, ''))};
  }

  // instrument range + frequency
  const noteSemi = semi(note);
  const [low, high] = ranges[instrument]?.map(semi) || [];
  const rangeState =
    !instrument ? '' :
    noteSemi < low || noteSemi > high ? 'out-of-range' :
    noteSemi === low || noteSemi === high ? 'edge' : 'in-range';
  const freq = (pitchStandard * 2 ** ((noteSemi - 57) / 12)).toFixed(2);

  useEffect(() => {
    document.querySelector('#output').className = rangeState;
  }, [rangeState]);

  const [markdown, setMarkdown] = useState("");
  const [showMarkdown, setShowMarkdown] = useState(false);

  useEffect(() => {
    fetch(file)
      .then((res) => res.text())
      .then((text) => setMarkdown(text));
  }, []);

  return (
    <>

    <h1>Notation Map</h1>
    <h2>Current&nbsp;note: <span className={rangeState}> {displayNote} </span></h2>
    <label
      // onClick={inputRef.current.focus()}
    >Note:&nbsp;
      <input
      style={{ display:'inline-block', maxWidth:'5.5em', textAlign:'center'}}
      ref={inputRef}
      placeholder={randomNote()}
      onChange={(e) => {
        setInputNote(e.target.value.replace(/[^a-gA-G#\d]/, '').slice(0, 3));
      }}
      value={inputNote}
    >

    </input>
    </label>

    <form
      action=""
      >
      <button
        type="submit"
        value="random"
        onClick={ e=> {
        e.preventDefault();
        setInputNote(randomNote());}
      }
      >
        <span className="underline">r</span>andom
      </button>
    </form>

    <form action=""
      className="accidentals"
      >
      <button
        type="button"
        onClick={()=>insertFlat()}
        >
          ♭
      </button>

      <button
        type="button"
        onClick={()=>naturalize()}
        >
          ♮
      </button>

      <button
        type="button"
        onClick={()=>insertSharp()}
        >
          ♯
      </button>

    </form>

    <aside id="controls">
      <form action="" className="instruments">
        {Object.keys(ranges).map((name) => (
          <button
            type="button"
            key={name}
            className={instrument === name ? 'underline' : ''}
            title={`${name} range: ${ranges[name][0]}–${ranges[name][1]}`}
            onClick={() => setInstrument(instrument === name ? '' : name)}
          >
            {name}
          </button>
        ))}
      </form>
      { instrument &&
        <p className={`range ${rangeState}`}>
          {ranges[instrument][0]}&#8202;–&#8202;{ranges[instrument][1]}
          {rangeState === 'edge' && <span> edge&nbsp;of&nbsp;range</span>}
          {rangeState === 'out-of-range' && <span> out&nbsp;of&nbsp;range</span>}
        </p>
      }
      <label className="pitch">
        A4&#8239;=&#8239;{pitchStandard}&#8239;Hz
        <input
          type="range"
          min="415"
          max="466"
          value={pitchStandard}
          title="pitch standard; double-click resets to 440"
          onDoubleClick={() => setPitchStandard(440)}
          onChange={(e) => setPitchStandard(+e.target.value)}
        />
      </label>
      <p id="freq">{displayNote}&#8239;≈&#8239;{freq}&#8239;Hz</p>
    </aside>

    <button id="markdown-button"
      onClick={()=> setShowMarkdown(!showMarkdown)}
    ><i>{showMarkdown ? 'x' : 'i'}</i></button>
    { showMarkdown && <section id="markdown">
      <Markdown children={markdown} />
    </section>
    }

    </>
  );
}

export default App;
